import React, { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../lib/language';
import { BaseStatementSearchParamType, StatementPeriodType } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';
import { getStatements as getStatementsCall, getStatementsIncome } from '../../lib/store/fundamental';

import {
  BaseTable,
  ClickableDiv,
  FinancialPeriodButtons,
  FinancialsTableRow,
  GoBackButton,
  MobileInfoBox,
  Preloader,
} from '../../components';
import Navigation from '../../components/Navigation/Navigation';
import SimpleFinancialTableRow from '../../components/SimpleFinancialTableRow/SimpleFinancialTableRow';
import { useIsMobile } from '../../hooks';
import TimeOptionsModal from '../SymbolDetails/Tabs/Ratios/Modals/TimeOptionsModal';

import styles from './IncomeStatement.module.scss';

type Props = {
  type: string;
  value: string;
};

const IncomeStatement = (props: ReduxProps) => {
  const { getAllStatements, getStatements, statementsData } = props;
  const isMobile = useIsMobile();

  const url = window.location.href;
  const symbol = url.split('/')[4];

  const [period, setPeriod] = useState <StatementPeriodType>(StatementPeriodType.ANNUAL);
  const [selectedPeriod, setSelectedPeriod] = useState <number>(1);
  const [headers, setHeaders] = useState <string[]>([]);
  const [data, setData] = useState <Array<Array<number | string>>>([]);
  const [loading, setLoading] = useState(true);
  const [currentTimeOption, setCurrentTimeOption] = useState(0);
  const [timeOptions, setTimeOptions] = useState<string[]>([]);
  const currentPeriod = currentTimeOption + 1;

  const tabs = [
    { text: lang.financialsAnnualOption(),
      component: <></>,
    },
    { text: lang.financialsQuarterlyOption(),
      component: <></>,
    },
  ];

  useEffect(() => {
    getAllStatements({
      symbol,
      periodType: period,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getStatements({
      symbol,
      periodType: period,
    });
  }, [getStatements, symbol, period]);

  useEffect(() => {
    if (statementsData.balanceSheet.length > 0) {
      const headersArray: string[] = [];
      const revenueArray: Array<number | string > = [];
      const costOfRevenueArray: Array<number | string > = [];
      const grossProfitArray: Array<number | string > = [];
      const operatingExpenseArray: Array<number | string > = [];
      const researchAndDevelopmentArray: Array<number | string > = [];
      const sellingAndAdminArray: Array<number | string > = [];
      const operatingIncomeArray: Array<number | string > = [];
      const totalOtherIncomeArray: Array<number | string > = [];
      const incomeBeforeTaxArray: Array<number | string > = [];
      const incomeTaxExpenseArray: Array<number | string > = [];
      const netIncomeArray: Array<number | string > = [];
      const basicEPSArray: Array<number | string > = [];
      const dilutedEPSArray: Array<number | string > = [];
      const weightedSharesArray: Array<number | string > = [];
      const dilWeightedSharesArray: Array<number | string > = [];
      const interestIncomeArray: Array<number | string > = [];
      const interestExpenseArray: Array<number | string > = [];
      const ebitdaArray: Array<number | string > = [];

      statementsData.income.forEach((item) => {
        // eslint-disable-next-line no-unused-expressions
        headersArray.push(period === StatementPeriodType.ANNUAL ? item.calendarYear.toString() : `${item.period}'${item.calendarYear.toString().slice(-2)}`);
        revenueArray.push(item.revenue);
        costOfRevenueArray.push(item.costOfRevenue);
        grossProfitArray.push(item.grossProfit);
        operatingExpenseArray.push(item.operatingExpenses);
        researchAndDevelopmentArray.push(item.researchAndDevelopmentExpenses);
        sellingAndAdminArray.push(item.sellingGeneralAndAdministrativeExpenses);
        operatingIncomeArray.push(item.operatingIncome);
        totalOtherIncomeArray.push(item.totalOtherIncomeExpensesNet);
        incomeBeforeTaxArray.push(item.incomeBeforeTax);
        incomeTaxExpenseArray.push(item.incomeTaxExpense);
        netIncomeArray.push(item.netIncome);
        basicEPSArray.push(item.eps);
        dilutedEPSArray.push(item.epsDiluted);
        weightedSharesArray.push(item.weightedAverageShsOut);
        dilWeightedSharesArray.push(item.weightedAverageShsOutDil);
        interestIncomeArray.push(item.interestIncome);
        interestExpenseArray.push(item.interestExpense);
        ebitdaArray.push(item.ebitda);
      });

      !isMobile && headersArray.push(lang.typeHeader());
      revenueArray.push(lang.financialsRevenue());
      costOfRevenueArray.push(lang.financialsCostOfRevenue());
      grossProfitArray.push(lang.financialsGrossProfit());
      operatingExpenseArray.push(lang.financialsOperatingExpenses());
      researchAndDevelopmentArray.push(lang.financialsResearchAndDevelopment());
      sellingAndAdminArray.push(lang.financialsSellingGeneralAndAdmin());
      operatingIncomeArray.push(lang.financialsOperatingIncome());
      totalOtherIncomeArray.push(lang.financialsTotalOtherIncomeExpense());
      incomeBeforeTaxArray.push(lang.financialsIncomeBeforeTax());
      incomeTaxExpenseArray.push(lang.financialsIncomeTaxExpense());
      netIncomeArray.push(lang.financialsNetIncome());
      basicEPSArray.push(lang.financialsBasicEPS());
      dilutedEPSArray.push(lang.financialsDilutedEPS());
      weightedSharesArray.push(lang.financialsWeightedAverageShares());
      dilWeightedSharesArray.push(lang.financialsDilutedWeightedAverageShares());
      interestIncomeArray.push(lang.financialsInterestIncome());
      interestExpenseArray.push(lang.financialsInterestExpense());
      ebitdaArray.push(lang.financialsEBITDA());

      setHeaders(headersArray.reverse());
      setData([
        revenueArray.reverse(),
        costOfRevenueArray.reverse(),
        grossProfitArray.reverse(),
        operatingExpenseArray.reverse(),
        researchAndDevelopmentArray.reverse(),
        sellingAndAdminArray.reverse(),
        operatingIncomeArray.reverse(),
        totalOtherIncomeArray.reverse(),
        incomeBeforeTaxArray.reverse(),
        incomeTaxExpenseArray.reverse(),
        netIncomeArray.reverse(),
        basicEPSArray.reverse(),
        dilutedEPSArray.reverse(),
        weightedSharesArray.reverse(),
        dilWeightedSharesArray.reverse(),
        interestIncomeArray.reverse(),
        interestExpenseArray.reverse(),
        ebitdaArray.reverse(),
      ]);
    }
  // Period shouldn't be a dependency here
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementsData, isMobile]);

  useEffect(() => {
    const options = headers.filter((header) => header !== lang.typeHeader() || !isMobile);
    setTimeOptions(options);
  }, [headers, isMobile]);

  useEffect(() => {
    setCurrentTimeOption(0);
  }, [period]);

  useEffect(() => {
    if (data.length > 0) {
      setLoading(false);
    }
  }, [data, setLoading]);

  const handlePeriodChange = (index: number) => {
    setPeriod(index === 0 ? StatementPeriodType.ANNUAL : StatementPeriodType.QUARTERLY);
  };

  if (loading) {
    return (
      <div className={styles.loader}>
        <Preloader isFullScreen text="" owner="income-statement" />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      {!isMobile && (
      <div className={styles.backButton}>
        <GoBackButton />
      </div>
      )}

      <div className={styles.header}>
        <div className={styles.title}>
          {lang.financialsIncomeStatement()}
        </div>
      </div>
      <div className={styles.periodButtonsWrapper}>
        <FinancialPeriodButtons currentPeriod={period} setPeriod={setPeriod} />
      </div>
      <div className={classNames(styles.tableWrapper, { [styles.tableWrapperMob]: isMobile })}>
        {isMobile ? (
          <>
            <div className={styles.periodButtons}>
              <TimeOptionsModal
                options={timeOptions}
                currentOption={timeOptions[currentTimeOption]}
                onTimeChange={(index) => setCurrentTimeOption(index)}
                isDisabled={false}
                currentPeriod={period}
              />
            </div>
            <SimpleFinancialTableRow
              type="Type"
              value={timeOptions[currentTimeOption] || 'N/A'}
            />
            <div className={styles.wrapperInfo}>
              <FinancialsTableRow data={[data[0], data[1]]} isClickable currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[2]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow
                data={[data[3], data[4], data[5]]}
                isClickable
                currentPeriod={currentPeriod}
              />
              <FinancialsTableRow data={[data[6], data[7]]} isClickable currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[8], data[9]]} isClickable currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[10]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[11]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[12]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[13]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[14]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[15]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[16]]} currentPeriod={currentPeriod} />
              <FinancialsTableRow data={[data[17]]} currentPeriod={currentPeriod} />
            </div>
          </>
        ) : (
          <BaseTable>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
            <FinancialsTableRow data={[data[0], data[1]]} isClickable />
            <FinancialsTableRow data={[data[2]]} bigLine />
            <FinancialsTableRow data={[data[3], data[4], data[5]]} isClickable bigLine />
            <FinancialsTableRow data={[data[6], data[7]]} isClickable bigLine />
            <FinancialsTableRow data={[data[8], data[9]]} isClickable bigLine />
            <FinancialsTableRow data={[data[10]]} bigLine />
            <FinancialsTableRow data={[data[11]]} bigLine />
            <FinancialsTableRow data={[data[12], data[13], data[14], data[15], data[16], data[17]]} />
          </BaseTable>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  statementsData: state.fundamental.statementsData,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getAllStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsCall(params)),
  getStatements: (params: BaseStatementSearchParamType) => dispatch(getStatementsIncome(params)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
    type ReduxProps = ConnectedProps<typeof connector>;

export default connector(IncomeStatement);
